/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Accordion, AccordionItem, Tabs, Tab, Select, SelectItem, DataTable, Link, InlineNotification, TabList, TabPanel, TabPanels } from '@carbon/react';
import classNames from 'classnames';
import { cleanUpKey } from '../utils/common';
import TestTool from './TestTool';
import Markdown from './Markdown';
import SecurityDisplay from './SecurityDisplay';
import CodeSnippetsDisplay from './CodeSnippetsDisplay';
import DefinitionDisplay from './DefinitionDisplay';
import EndpointSelector from './EndpointSelector';
import ExtensionsDisplay from './ExtensionsDisplay';
import ResponsesDisplay from './ResponsesDisplay';
import Callback from './Callback';
import { CodeViewer } from './CodeViewer';

import { getTranslatedExtensions } from '../utils/translate';
import scrollIntoView from 'scroll-into-view';
import OperationTag from './OperationTag';
import ExternalLinkDecorator from './ExternalLinkDecorator';
import { useTranslation } from 'react-i18next';
import { useApi } from '../contexts/ApiHandler';
const { Table, TableRow, TableBody, TableCell, TableHeader } = DataTable;

const Operation = ({ isCallback, operation, operationPath, operationMethod, apps, options }) => {
  const [isWebsocket, setIsWebsocket] = useState(false);
  const [config, setConfig] = useState({
    accept: null,
    contenttype: null,
    endpoint: {},
    selectedSecurityOption: 0
  });
  const { t } = useTranslation();
  const { api, apiHandler } = useApi();

  useEffect(() => {
    let element = document.getElementsByClassName('operationHeader')[0];
    scrollIntoView(element);

    if (operation || api) {
      // Assert the correct handler for the api

      let endpointOptions = { path: operationPath, method: operationMethod };
      let endpoint = apiHandler?.getEndpoints(api, endpointOptions);
      if (endpoint) {
        let newConfig = {};

        if (endpoint && endpoint[0] && endpoint[0].url) {
          endpoint = endpoint[0].url;
        } else {
          endpoint = 'https://undefined';
        }
        if (Object.keys(config.endpoint).length !== 0) {
          endpoint = config.endpoint;
        }
        if (endpoint && endpoint.url) {
          endpoint = endpoint.url;
        }
        let newIsWebsocket = false;
        if (/(wss?):\/\//.test(endpoint)) {
          newIsWebsocket = true;
        }

        setIsWebsocket(newIsWebsocket);
        newConfig.endpoint = endpoint;

        if (newIsWebsocket) {
          newConfig.options = { languages: ['node'], defaultLanguage: 'node' };
        } else {
          // need to make updates to the code snippet config structure too
          newConfig.accept = apiHandler?.getDefaultAcceptHeader(operation, api);
          newConfig.contenttype = apiHandler?.getDefaultContentTypeHeader(operation, api);
        }
        newConfig.selectedSecurityOption = 0;
        setConfig(newConfig);
      }
    }
  }, [api, apiHandler, config.endpoint, operation, operationMethod, operationPath]);

  function updateCodeSnippetConfig(evt, index) {
    let newCodeSnippetConfig = Object.assign({}, config);

    if (evt.target.id === 'Accept') {
      newCodeSnippetConfig.accept = evt.target.value;
    } else if (evt.target.id === 'Content-Type') {
      newCodeSnippetConfig.contenttype = evt.target.value;
    } else if (evt.target.id === 'SecurityOption') {
      newCodeSnippetConfig.selectedSecurityOption = index;
    } else if (evt.target.id === 'operation-endpoint-select' || evt.target.id === 'testtool-endpoint-select') {
      newCodeSnippetConfig.endpoint = evt.target.value;
    }
    setConfig(newCodeSnippetConfig);
  }
  function renderParameters(parameterType, contentType) {
    let parametersToRender = [];
    let allParameters = apiHandler?.getParameters(operation, operationPath, contentType, api, isCallback);
    let chosenParameterNames = apiHandler?.getParametersByType(parameterType, operation, operationPath, contentType, api);

    let definitionOptions = {};
    if (isCallback) {
      definitionOptions = { isCallback, operation: api };
    }

    let markupType = apiHandler?.getMarkupType();

    let maxDepth = 9;
    if (options && typeof options.apiMaxDepth !== 'undefined') {
      maxDepth = options.apiMaxDepth;
    }

    if (chosenParameterNames && chosenParameterNames.length > 0) {
      chosenParameterNames.forEach(function (parameterName) {
        if (contentType !== 'Header' || (parameterName.toLowerCase() !== 'content-type' && parameterName.toLowerCase() !== 'accept')) {
          parametersToRender.push(allParameters[parameterName]);
        }
      });
    }

    if (parameterType === 'header') {
      if (!isWebsocket) {
        let acceptsTypes = apiHandler?.getAccepts(operation, api);

        if (acceptsTypes && acceptsTypes.length > 0) {
          let acceptsParameter = {};
          acceptsParameter.name = 'Accept';
          acceptsParameter.in = 'header';
          acceptsParameter.description = acceptsTypes.length > 1 ? t('possible_values') : null;
          acceptsParameter.valueChoices = [];

          acceptsTypes.forEach(function (accept) {
            acceptsParameter.valueChoices.push(accept);
          });

          parametersToRender.push(acceptsParameter);
        }

        let contentTypes = apiHandler?.getContentTypes(operation, api);
        if (contentTypes && contentTypes.length > 0) {
          let contentTypeParameter = {};
          contentTypeParameter.name = 'Content-Type';
          contentTypeParameter.in = 'header';
          contentTypeParameter.description = contentTypes.length > 1 ? t('possible_values') : null;
          contentTypeParameter.valueChoices = [];

          contentTypes.forEach(function (contentType) {
            contentTypeParameter.valueChoices.push(contentType);
          });

          parametersToRender.push(contentTypeParameter);
        }
      }

      // For the case where an API requires invokers to provide their application certificate
      // we need to add another header (supports mutual TLS per https://www.ibm.com/support/knowledgecenter/en/SSMNED_5.0.0/com.ibm.apic.toolkit.doc/task_apionprem_creating_apis.html#task_tq2_11r_xt__tls_mutual_auth)
      if (
        options.showMTLSHeader &&
        api &&
        api['x-ibm-configuration'] &&
        api['x-ibm-configuration']['application-authentication'] &&
        api['x-ibm-configuration']['application-authentication']['certificate'] === true
      ) {
        let clientCertificateHeader = {};
        clientCertificateHeader.name = 'X-Client-Certificate';
        clientCertificateHeader.in = 'header';
        clientCertificateHeader.required = true;
        clientCertificateHeader.type = 'string';
        clientCertificateHeader.description = t('x_client_certficate_header');

        parametersToRender.push(clientCertificateHeader);
      }
    }

    if (!parametersToRender || parametersToRender.length === 0) {
      return null;
    }

    return (
      <AccordionItem className={classNames(`parameter-${parameterType}`)} key={parameterType} title={t(parameterType)} open={true}>
        <Table className={'parameterTable'}>
          <TableBody>
            {parametersToRender.map((parameter) => {
              let parameterExtensions = apiHandler?.getCustomExtensions(parameter);
              let schema = parameter;
              if (schema.schema) {
                schema = schema.schema;
              } else if (schema.content && schema.content[Object.keys(schema.content)[0]].schema) {
                schema = schema.content[Object.keys(schema.content)[0]].schema;
              }
              return (
                <TableRow className={classNames(`parameter-${parameterType}-${parameter.name}`)} key={`parameter_${parameter.name}`}>
                  <TableHeader className="parameterNameContainer">
                    <div className="parameterName">{parameter.name}</div>
                    {parameter.required && <div className="requiredHighlight parameterIn">{t('required')}</div>}
                    {!parameter.required && <div className="parameterIn">{t('optional')}</div>}
                    {parameter.deprecated && <div className="parameterDeprecated">deprecated</div>}
                  </TableHeader>
                  <TableCell className="parameterDescriptionContainer">
                    {parameter.description && (
                      <Markdown className="parameterDescription" data={getTranslatedExtensions(parameter, 'description')} markupType={markupType} />
                    )}
                    {schema.type && (
                      <div className="parameterType schema">
                        <div>{schema.type}</div>
                        {schema.format && <div>({schema.format})</div>}
                        {schema.items && schema.items.type && <div>[{schema.items.type}]</div>}
                        {schema.items && Array.isArray(schema.items.oneOf) && <div>[{schema.items.oneOf.join(' | ')}]</div>}
                        {schema.items && schema.items === {} && <div>[any]</div>}
                        {schema.nullable && <div>&nbsp;| null</div>}
                      </div>
                    )}
                    {schema.discriminator && schema.discriminator.propertyName && (
                      <div className="parameterDiscriminator schema">discriminator: {schema.discriminator.propertyName}</div>
                    )}
                    {schema.default !== undefined && <div className="parameterDefault schema">default: {JSON.stringify(schema.default)}</div>}
                    {parameter.style && <div className="parameterStyle schema">style: {parameter.style}</div>}

                    {parameter.explode !== undefined && <div className="parameterExplode schema">explode: {String(parameter.explode)}</div>}
                    {parameter.allowEmptyValue !== undefined && (
                      <div className="parameterAllowEmptyValue schema">allowEmptyValue: {String(parameter.allowEmptyValue)}</div>
                    )}
                    {parameter.allowReserved !== undefined && (
                      <div className="parameterAllowReserved schema">allowReserved: {String(parameter.allowReserved)}</div>
                    )}

                    {schema.pattern && schema.type === 'string' && <div className="parameterPattern schema">pattern: {schema.pattern}</div>}
                    {schema.minLength !== undefined && schema.type === 'string' && (
                      <div className="parameterMinLength schema">minLength: {schema.minLength}</div>
                    )}
                    {schema.maxLength !== undefined && schema.type === 'string' && (
                      <div className="parameterMaxLength schema">maxLength: {schema.maxLength}</div>
                    )}

                    {schema.minimum !== undefined && (schema.type === 'number' || schema.type === 'integer') && (
                      <div className="parameterMinimum schema">
                        minimum{schema.exclusiveMinimum && <span>&nbsp;(exclusive)</span>}: {schema.minimum}
                      </div>
                    )}
                    {schema.maximum !== undefined && (schema.type === 'number' || schema.type === 'integer') && (
                      <div className="parameterMaximum schema">
                        maximum{schema.exclusiveMaximum && <span>&nbsp;(exclusive)</span>}: {schema.maximum}
                      </div>
                    )}
                    {schema.multipleOf !== undefined && (schema.type === 'number' || schema.type === 'integer') && (
                      <div className="parameterMultipleOf schema">multipleOf: {schema.multipleOf}</div>
                    )}

                    {schema.maxItems !== undefined && schema.type === 'array' && <div className="parameterMaxItems schema">maxItems: {schema.maxItems}</div>}
                    {schema.minItems !== undefined && schema.type === 'array' && <div className="parameterMinItems schema">minItems: {schema.minItems}</div>}
                    {schema.uniqueItems !== undefined && schema.type === 'array' && (
                      <div className="parameterUniqueItems schema">uniqueItems: {String(schema.uniqueItems)}</div>
                    )}
                    {parameter.collectionFormat && parameter.type === 'array' && (
                      <div className="parameterCollectionFormat schema">collectionFormat: {parameter.collectionFormat}</div>
                    )}
                    {(schema.additionalProperties === false || (schema.additionalProperties && schema.additionalProperties.type)) &&
                      schema.type === 'object' && (
                        <div className="parameterAdditionalProperties schema">
                          {!schema.additionalProperties && <div>additionalProperties: false</div>}
                          {schema.additionalProperties && schema.additionalProperties.type && (
                            <div>additionalProperties: {schema.additionalProperties.type}</div>
                          )}
                        </div>
                      )}
                    {schema.maxProperties !== undefined && schema.type === 'object' && (
                      <div className="parameterMaxProperties schema">maxProperties: {schema.maxProperties}</div>
                    )}
                    {schema.minProperties !== undefined && schema.type === 'object' && (
                      <div className="parameterMinProperties schema">
                        <div>minProperties: {schema.minProperties}</div>
                      </div>
                    )}

                    {parameterExtensions && <ExtensionsDisplay className="parameterExtensions" extensions={parameterExtensions} />}
                    {parameter.valueChoices && parameter.valueChoices.length === 1 && <div key={parameter.name}>{parameter.valueChoices[0]} </div>}
                    {parameter.valueChoices && parameter.valueChoices.length > 1 && (
                      <Select hideLabel id={parameter.name} key={`${parameter.name}`} onChange={updateCodeSnippetConfig} defaultValue="application/json">
                        {parameter.valueChoices.map(function (value) {
                          return <SelectItem key={`${parameter.name}_${value}`} text={value} value={value} />;
                        })}
                      </Select>
                    )}
                    {!parameter.valueChoices && (
                      <DefinitionDisplay
                        pathName={operationPath}
                        method={operationMethod}
                        object={parameter}
                        contentType={contentType}
                        maxDepth={maxDepth}
                        options={definitionOptions}
                        viewMode="schema"
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </AccordionItem>
    );
  }

  function renderServerVariables() {
    let basePlusOpPath = apiHandler?.getOperationPath(api, operationPath);
    let variables = apiHandler?.getServerVariables(api, config.endpoint, { path: basePlusOpPath, method: operationMethod });
    let markupType = apiHandler?.getMarkupType();
    if (!variables) {
      return;
    }

    return (
      <div className="operationInformation operationSummary operationServerVariables">
        <div role="heading" aria-level={3}  className="labelText">{t('server_variables')}</div>
        <div className="serverVariables">
          <Table className={'serverVariableTable'}>
            <TableBody>
              {variables &&
                Object.entries(variables).map((entry) => {
                  let variableName = entry[0];
                  let variable = entry[1];
                  return (
                    <TableRow className={classNames(`server-variable-${variableName}`)} key={`server_variable_${variableName}`}>
                      <TableCell className="serverVariableNameContainer">
                        <div className="serverVariableName">{variableName}</div>
                      </TableCell>
                      <TableCell className="serverVariableDescriptionContainer">
                        {variable.description && (
                          <Markdown className="serverVariableDescription" data={getTranslatedExtensions(variable, 'description')} markupType={markupType} />
                        )}
                        {variable.enum && (
                          <Select
                            hideLabel
                            id={variableName}
                            key={`${variableName}`}
                            onChange={updateCodeSnippetConfig}
                            defaultValue={variable.default}
                          >
                            {variable.enum.map(function (value) {
                              return <SelectItem key={`${variableName}_${value}`} text={String(value)} value={value} />;
                            })}
                          </Select>
                        )}
                        {!variable.enum && (
                          <div className={classNames('cds--snippet', 'cds--snippet--multi')}>
                            <CodeViewer>{variable.default}</CodeViewer>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }
  function renderSoapBody() {
    let parametersToRender = [];

    let allParameters = apiHandler?.getParameters(operation, operationPath, 'application/xml', api);
    let chosenParameterNames = apiHandler?.getParametersByType('body', operation, operationPath, 'application/xml', api);

    let maxDepth = 9;
    if (options && typeof options.apiMaxDepth !== 'undefined') {
      maxDepth = options.apiMaxDepth;
    }

    if (chosenParameterNames && chosenParameterNames.length === 1) {
      chosenParameterNames.map(function (parameterName) {
        parametersToRender.push(allParameters[parameterName]);
        return null;
      });
    }

    if (!parametersToRender || parametersToRender.length !== 1) {
      return null;
    }

    return (
      <DefinitionDisplay
        pathName={operationPath}
        method={operationMethod}
        object={parametersToRender[0]}
        contentType={'application/xml'}
        maxDepth={maxDepth}
        viewMode="schema"
      />
    );
  }

  let contentType = config.contenttype;
  if (!contentType) {
    contentType = apiHandler?.getDefaultContentTypeHeader(operation, api);
  }
  let removedHeaders = false;
  let allParameters = apiHandler?.getParameters(operation, operationPath, contentType, api);
  if (allParameters !== undefined && Object.keys(allParameters) && Object.keys(allParameters).length > 0) {
    removedHeaders = apiHandler?.containsReservedParameters(allParameters);
  }

  let isSoap = apiHandler?.isSOAP(api);
  let isTestable = options.tryIt && apiHandler?.isTestable(api);
  let isEnforced = apiHandler?.isEnforced();

  let accept = config.accept;
  if (!accept) {
    accept = apiHandler?.getDefaultAcceptHeader(operation, api);
  }

  let maxDepth = 9;
  if (options && typeof options.apiMaxDepth !== 'undefined') {
    maxDepth = options.apiMaxDepth;
  }

  let endpointOptions = { path: operationPath, method: operationMethod };
  let endpoints = apiHandler?.getEndpoints(api, endpointOptions);
  let basePlusOpPath = apiHandler?.getOperationPath(api, operationPath);
  let operationExtensions = apiHandler?.getCustomExtensions(operation);
  let mtlsEnabled = api && api['x-ibm-configuration'] && api['x-ibm-configuration']['application-authentication'];
  let apiTags = apiHandler?.getApiTags(api);
  let markupType = apiHandler?.getMarkupType();
  const security = apiHandler?.getSecurity(operation);
  const securityDefinitions = apiHandler?.getSecurityDefinitions(config.endpoint);
  const hasSecurity = Array.isArray(security) && security.length > 0 && securityDefinitions && Object.keys(securityDefinitions).length > 0;

  getTranslatedExtensions(operation, 'summary');

  const snippetInfo = {
    config: config,
    operation: operation,
    pathName: operationPath,
    method: operationMethod,
    maxDepth: maxDepth
  };

  let parametersToRender = [];
  let parameterTypes = ['header', 'cookie', 'path', 'query', 'formData', 'body'];
  parameterTypes.forEach((type) => {
    let renderedParameters = renderParameters(type, contentType);
    if (renderedParameters) {
      parametersToRender.push(renderedParameters);
    }
  });

  return (
    <div
      className={classNames(
        'apiconnect-explorer',
        isCallback
          ? classNames('callback-section', 'main', `callback-path-${cleanUpKey(operationPath)}`, `callback-path-${cleanUpKey(operationPath)}`)
          : classNames('operation-section', 'main', `operation-method-${cleanUpKey(operationMethod)}`, `operation-path-${cleanUpKey(operationPath)}`),
        isSoap ? 'soap-api' : 'rest-api'
      )}
    >
      {removedHeaders && (
        <InlineNotification
          kind="warning"
          title={t('reserved_title')}
          subtitle={t('reserved_subtitle')}
          className="errorFrame"
          iconDescription={t('close_swagger_errors')}
        />
      )}
      {!isCallback && (
        <section className="operationHeader" aria-label="operation-header">
          <div className="operationTitle" data-testid="operationTitle">
            {operation.summary && <h2>{getTranslatedExtensions(operation, 'summary')}</h2>}
            {!operation.summary && operation.operationId && <h2>{operation.operationId}</h2>}
            {!operation.summary && !operation.operationId && operationPath && operationMethod && (
              <h2>
                {operationMethod.toUpperCase()} : {operationPath}
              </h2>
            )}
            {operation.deprecated && <div className={'deprecatedLabel'}>{t('operation_deprecated')}</div>}
          </div>
          <div className="operationTags tagsContainer">
            {operation['tags'] &&
              operation['tags'].map(function (tag) {
                return <OperationTag key={tag} tag={tag} apiTags={apiTags} markupType={markupType} />;
              })}
          </div>
        </section>
      )}
      <section className="operationDisplay" aria-label="operation-display">
        <Tabs
          className={classNames(isCallback ? 'notRendered' : 'op-tabs')}
          aria-label="operation tabs"
          tabContentClassName={classNames(isCallback ? 'callback-tab' : 'operation-tab')}
        >
          <TabList aria-label="operation tabs">
            {[<Tab key='detailsTab' id="detailsTab" className="detailsTab" title={t('details_header')}>
              {t('details_header')}{' '}
            </Tab>,
            operation.callbacks && (
              <Tab key='callbacksTab' id="callbacksTab" className="callbacksTab" title={t('callbacks')}>
                {t('callbacks')}
              </Tab>
            ),
            isTestable && !isCallback && (
              <Tab key='testTab' id="testTab" className="testTab" title={t('try')}>
                {t('try')}
              </Tab>
            )].filter(Boolean)}
          </TabList>
          <TabPanels>
            {[
            <TabPanel key='operation'>
              <section className={classNames('tab-content', 'operationDefinition')} aria-label="operation-definition">
                {hasSecurity && !isEnforced && (
                  <InlineNotification
                    kind="info"
                    className={'operation__information'}
                    title={t('request_security_credentials')}
                    hideCloseButton={true}
                    lowContrast={true}
                  />
                )}
                <div className="operationInformation operationSummary">
                  <div role="heading" aria-level={3}  className={classNames('labelText', { method: !isSoap }, operationMethod)}>
                    {!isSoap && operationMethod}
                    {isSoap && t('endpoint')}
                  </div>
                  <div className="valueText">
                    {isCallback ? (
                      basePlusOpPath
                    ) : (
                      <EndpointSelector
                        id="operation-endpoint-select"
                        endpoints={endpoints}
                        opPath={basePlusOpPath}
                        onChange={updateCodeSnippetConfig}
                        endpoint={config.endpoint}
                      />
                    )}
                  </div>
                </div>
                {operation.description && (
                  <div className="operationInformation operationDescription">
                    <div role="heading" aria-level={3}  className="labelText">{t('description')}</div>
                    <div className="valueText">
                      <Markdown className="operationDescription" data={getTranslatedExtensions(operation, 'description')} markupType={markupType} />
                    </div>
                  </div>
                )}
                {operation.externalDocs && (
                  <div className="operationInformation operationExternalLinks">
                    <div role="heading" aria-level={3} className="labelText">{t('explorer_external_links')}</div>
                    <div className="valueText">
                      {operation.externalDocs.description && (
                        <Markdown
                          className={'externalLinkDescription'}
                          data={getTranslatedExtensions(operation.externalDocs, 'description')}
                          markupType={markupType}
                        />
                      )}
                      <Link href={operation.externalDocs.url} target="_blank" rel="noopener noreferrer">
                        {operation.externalDocs.url}
                        <ExternalLinkDecorator />
                      </Link>
                    </div>
                  </div>
                )}
                {hasSecurity && (
                  <SecurityDisplay
                    key="operationSecurityDefinitions"
                    onChange={updateCodeSnippetConfig}
                    markupType={markupType}
                    mtls={mtlsEnabled}
                    security={security}
                    securityDefinitions={securityDefinitions}
                    showMultipleSecurityOptionsWarning={true}
                  />
                )}
                {operationExtensions && <ExtensionsDisplay label={t('extensions')} className="operationExtensions" extensions={operationExtensions} />}
                {!isSoap && renderServerVariables()}
                {!isSoap && parametersToRender.length > 0 && (
                  <div className="operationInformation operationSummary operationParms">
                    <div role="heading" aria-level={3}  className="labelText">{t('parameters')}</div>
                    <div className="operationParameters">
                      <Accordion align="start">{parametersToRender}</Accordion>
                    </div>
                  </div>
                )}
                {isSoap && (
                  <div className="operationInformation operationSummary">
                    <div role="heading" aria-level={3}  className="labelText">{t('request')}</div>
                    <div className="valueText">{renderSoapBody()}</div>
                  </div>
                )}
                {(!isSoap || (isSoap && options.showSoapCodeSnippets)) && !isCallback && (
                  <div className="operationInformation operationSummary requestCodeSnippets exampleRequest">
                    <div role="heading" aria-level={3}  className="labelText">{t('example_request')}</div>
                    <CodeSnippetsDisplay key={'exampleRequest'} snippetType={'operation'} snippetInfo={snippetInfo} />
                  </div>
                )}
                {!isWebsocket && (
                  <ResponsesDisplay
                    api={api}
                    operation={operation}
                    operationMethod={operationMethod}
                    operationPath={operationPath}
                    accept={accept}
                    options={options}
                  />
                )}
              </section>
            </TabPanel>,
            operation.callbacks && (
              <TabPanel key='callback'>
                {Object.keys(operation.callbacks).map((event) => {
                  return <Callback key={event} event={event} operation={operation} callbacks={operation.callbacks[event]} apps={apps} />;
                })}
              </TabPanel>
            ),
            isTestable && !isCallback && (
              <TabPanel key='testTool' id="testTab" className="testTab" label={t('try')} title={t('try')}>
                <TestTool
                  api={api}
                  operation={operation}
                  operationPath={operationPath}
                  operationMethod={operationMethod}
                  options={options}
                  apps={apps}
                  initialEndpoint={config.endpoint}
                  onChangeEndpoint={updateCodeSnippetConfig}
                />
              </TabPanel>
            )].filter((tab) => tab)}
          </TabPanels>
        </Tabs>
      </section>
    </div>
  );
};

// needed for finding in tests
Operation.displayName = 'Operation';
Operation.propTypes = {
  api: PropTypes.object,
  operation: PropTypes.object,
  operationPath: PropTypes.string,
  operationMethod: PropTypes.string,
  options: PropTypes.object,
  apps: PropTypes.array
};

export default withTranslation()(Operation);
